/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener("DOMContentLoaded", function() {
  const forms = document.querySelectorAll("[data-inline-predictive-search]");

  if (forms.length === 0 || !window.themeVariables || !window.themeVariables.routes) {
    return;
  }

  const supportsPredictiveSearch = function() {
    const shopifyFeatures = document.getElementById("shopify-features");

    if (!shopifyFeatures) {
      return false;
    }

    try {
      return JSON.parse(shopifyFeatures.innerHTML).predictiveSearch;
    } catch (error) {
      return false;
    }
  };

  const predictiveSearchEnabled = supportsPredictiveSearch();
  const compactSearchMediaQuery = window.matchMedia("(max-width: 1020px)");

  const debounce = function(callback, delay) {
    let timeoutId = null;

    return function() {
      const args = arguments;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function() {
        callback.apply(null, args);
      }, delay);
    };
  };

  const hideResults = function(resultsElement) {
    resultsElement.hidden = true;
    resultsElement.innerHTML = "";
  };

  const getSearchResults = async function(query, abortController) {
    const url = predictiveSearchEnabled
      ? `${window.themeVariables.routes.predictiveSearchUrl}?q=${encodeURIComponent(query)}&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search`
      : `${window.themeVariables.routes.searchUrl}?q=${encodeURIComponent(query)}&resources[limit]=50&section_id=predictive-search-compatibility`;
    const response = await fetch(url, { signal: abortController.signal });
    const wrapper = document.createElement("div");

    wrapper.innerHTML = await response.text();

    return wrapper.firstElementChild ? wrapper.firstElementChild.innerHTML : "";
  };

  forms.forEach(function(form, formIndex) {
    const input = form.querySelector('[name="q"]');
    const resultsElement = form.parentElement.querySelector("[data-inline-predictive-search-results]");
    const resultsIdPrefix = `inline-predictive-search-${formIndex}`;
    let abortController = null;

    if (!input || !resultsElement) {
      return;
    }

    const buildSearchUrl = function(query) {
      const formData = new FormData(form);
      const params = new URLSearchParams(formData);

      params.set("q", query);

      return `${form.getAttribute("action")}?${params.toString()}`;
    };

    const scopeSearchResults = function(html, query) {
      const wrapper = document.createElement("div");

      wrapper.innerHTML = html;

      if (compactSearchMediaQuery.matches) {
        const productResults = document.createElement("div");
        const productList = document.createElement("ul");

        productList.className = "inline-predictive-search__list list--unstyled";

        wrapper.querySelectorAll('.predictive-search__product-list[data-type="products"] .predictive-search__product-item').forEach(function(item) {
          productList.appendChild(item.cloneNode(true));
        });

        if (productList.children.length > 0) {
          productResults.className = "inline-predictive-search__products";
          productResults.appendChild(productList);
          productResults.insertAdjacentHTML("beforeend", `<a class="inline-predictive-search__view-all" href="${buildSearchUrl(query)}">View all results</a>`);
          wrapper.innerHTML = "";
          wrapper.appendChild(productResults);
        }
      } else if (wrapper.querySelector(".predictive-search__results-categories")) {
        wrapper.insertAdjacentHTML("beforeend", `<a class="inline-predictive-search__view-all" href="${buildSearchUrl(query)}">View all results</a>`);
      }

      wrapper.querySelectorAll("[id]").forEach(function(element) {
        element.id = `${resultsIdPrefix}-${element.id}`;
      });
      wrapper.querySelectorAll("[aria-controls]").forEach(function(element) {
        element.setAttribute("aria-controls", `${resultsIdPrefix}-${element.getAttribute("aria-controls")}`);
      });

      return wrapper.innerHTML;
    };

    const runSearch = debounce(async function() {
      const query = input.value.trim();

      if (abortController) {
        abortController.abort();
      }

      if (query.length < 2) {
        hideResults(resultsElement);
        return;
      }

      abortController = new AbortController();
      resultsElement.classList.toggle("inline-predictive-search--compact", compactSearchMediaQuery.matches);
      resultsElement.hidden = false;
      resultsElement.innerHTML = '<div class="inline-predictive-search__loading">Searching...</div>';

      try {
        const html = await getSearchResults(query, abortController);
        resultsElement.innerHTML = scopeSearchResults(html, query);
        resultsElement.hidden = false;
      } catch (error) {
        if (error.name !== "AbortError") {
          hideResults(resultsElement);
        }
      }
    }, 200);

    input.addEventListener("input", runSearch);

    input.addEventListener("focus", function() {
      if (resultsElement.innerHTML.trim() !== "") {
        resultsElement.hidden = false;
      }
    });

    form.addEventListener("submit", function() {
      hideResults(resultsElement);
    });

    resultsElement.addEventListener("click", function(event) {
      if (event.target.closest('[data-action="reset-search"]')) {
        input.value = "";
        input.focus();
        hideResults(resultsElement);
      }
    });
  });

  document.addEventListener("click", function(event) {
    forms.forEach(function(form) {
      const resultsElement = form.parentElement.querySelector("[data-inline-predictive-search-results]");

      if (resultsElement && !form.parentElement.contains(event.target)) {
        resultsElement.hidden = true;
      }
    });
  });
});
